import { useEffect, useRef, useState } from 'react';
import { useSizes } from '@/utility/responsive.ts';

type Message = {
	content: string;
	sender: string;
	timestamp: string;
};

type VideoCallMessagesProps = {
	isActive: boolean;
	userInfos: {
		studentName: string;
		studentAvatar: string;
		studentId?: number;
	} | null;
	incomingMessages: Message[];
	onMessageSent: (content: string) => void;
	onToggle: () => void;
	dopingTechComponentsCacheStr: string;
};

export const MessagesVWOutActions = {
	setStudentInfo: 'SET_STUDENT_INFO',
	addMessage: 'ADD_MESSAGE',
	setMessages: 'SET_MESSAGES',
	clearMessages: 'CLEAR_MESSAGES'
};

export const MessagesVWInActions = {
	messageSent: 'MESSAGE_SENT',
	toggleExpand: 'TOGGLE_EXPAND'
};

const VideoCallMessages = ({ isActive, userInfos, incomingMessages, onMessageSent, onToggle, dopingTechComponentsCacheStr }: VideoCallMessagesProps) => {
	const [url, setUrl] = useState('');
	const [isIframeReady, setIsIframeReady] = useState(false);

	const { isMobile } = useSizes();

	const messagesIframeRef = useRef<HTMLIFrameElement | null>(null);
	const lastMessageCountRef = useRef(0);
	const loadEndTimeoutRef = useRef<any>();
	const dopingTechComponentsCacheStrRef = useRef('current');
	const incomingMessagesRef = useRef(incomingMessages);

	useEffect(() => {
		incomingMessagesRef.current = incomingMessages;
	}, [incomingMessages]);

	const sendMessage = (payload: object): void => {
		messagesIframeRef.current?.contentWindow?.postMessage(payload, '*');
	};

	const onMessage = (event: MessageEvent): void => {
		const data = event?.data;

		if (data?.type === MessagesVWInActions.messageSent) {
			onMessageSent(data.data.content);
		} else if (data?.type === MessagesVWInActions.toggleExpand) {
			onToggle();
		}
	};

	const onLoadEnd = () => {
		clearTimeout(loadEndTimeoutRef?.current);

		loadEndTimeoutRef.current = setTimeout(() => {
			const initializeData = {
				name: userInfos?.studentName || '',
				avatar: userInfos?.studentAvatar || '',
				id: userInfos?.studentId || 1,
				isMobile: isMobile,
				isExpert: true
			};

			sendMessage({
				type: MessagesVWOutActions.setStudentInfo,
				data: initializeData
			});

			setTimeout(() => {
				const messages = incomingMessagesRef.current;

				messages.forEach(message => {
					sendMessage({
						type: MessagesVWOutActions.addMessage,
						data: {
							content: message.content,
							sender: message.sender,
							timestamp: message.timestamp
						}
					});
				});

				lastMessageCountRef.current = messages.length;
				setIsIframeReady(true);
			}, 500);
		}, 300);
	};

	const onInit = (): void => {
		messagesIframeRef.current = document.getElementById('messages-iframe') as HTMLIFrameElement;

		if (messagesIframeRef.current) {
			messagesIframeRef.current.onload = () => {
				onLoadEnd();
			};
		}

		setUrl('');
		setTimeout(() => {
			setUrl(process.env.DOPI_TECH_COMPONENTS_URL + `?v=${dopingTechComponentsCacheStrRef?.current}` + '/#/video-call-messages');
		}, 100);
	};

	useEffect(() => {
		if (isActive && userInfos) {
			onInit();
			window.addEventListener('message', onMessage);

			return () => {
				setUrl('');
				window.removeEventListener('message', onMessage);
				lastMessageCountRef.current = 0;
				setIsIframeReady(false);
			};
		}
	}, [isActive, userInfos]);

	useEffect(() => {
		if (dopingTechComponentsCacheStr) {
			dopingTechComponentsCacheStrRef.current = dopingTechComponentsCacheStr;
		}
	}, [dopingTechComponentsCacheStr]);

	useEffect(() => {
		if (isIframeReady && incomingMessages.length > lastMessageCountRef.current && messagesIframeRef.current?.contentWindow) {
			const newMessages = incomingMessages.slice(lastMessageCountRef.current);

			newMessages.forEach(message => {
				if (message.sender === 'USER') {
					return;
				}

				sendMessage({
					type: MessagesVWOutActions.addMessage,
					data: {
						content: message.content,
						sender: message.sender,
						timestamp: message.timestamp
					}
				});
			});

			lastMessageCountRef.current = incomingMessages.length;
		}
	}, [incomingMessages, isIframeReady]);

	if (!isActive) return null;

	return (
		<>
			<iframe
				id="messages-iframe"
				className="meet-iframe messages-iframe"
				src={url}
				scrolling="no"
				allow="autoplay; camera; microphone; display-capture"
				allowFullScreen
			/>
		</>
	);
};

export default VideoCallMessages;
